import { SessionStatus } from "./types/models";

export const WEBINAR_TIMEZONE = "Asia/Kolkata" as const;

export const WEBINAR_PAGE_SIZE_DEFAULT = 10;

/** Backend capabilities — flip on when endpoints exist. */
export const WEBINAR_API = {
  canPublish: true,
  canCancel: true,
  canDeleteDraft: true,
  canReinstate: false,
  /** Cohort targeting API not ready — members maps to isForPaidUserOnly only. */
  canUseCohorts: false,
  canLiveConsole: false,
  canViewReport: false,
} as const;

export const STEP_LABELS = [
  "Basics",
  "Content",
  "Audience & access",
  "Schedule & delivery",
] as const;

export const STATUS_FILTERS: Array<{ key: "all" | SessionStatus; label: string }> =
  [
    { key: "all", label: "All" },
    { key: "draft", label: "Draft" },
    { key: "scheduled", label: "Scheduled" },
    { key: "live", label: "Live" },
    { key: "ended", label: "Ended" },
    { key: "cancelled", label: "Cancelled" },
  ];

export const STATUS_META: Record<
  SessionStatus,
  { label: string; bg: string; fg: string; dot: string }
> = {
  draft: { label: "DRAFT", bg: "#F5F1EC", fg: "#5C554E", dot: "#A09890" },
  // Patient-route brand: primary #299D6B, dark #238A5E, soft #E7F5EF
  scheduled: { label: "SCHEDULED", bg: "#E7F5EF", fg: "#238A5E", dot: "#299D6B" },
  live: { label: "LIVE", bg: "#F2E6EC", fg: "#A64D7E", dot: "#D9553F" },
  ended: { label: "ENDED", bg: "#F2EEE9", fg: "#5C554E", dot: "#C6BFB7" },
  cancelled: { label: "CANCELLED", bg: "#F9EFEA", fg: "#B4483A", dot: "#EB8771" },
};

export const CANCEL_REASONS = [
  "Host unavailable",
  "Low registration",
  "Technical issue",
  "Content not ready",
  "Other",
] as const;

export const COHORT_OPTIONS = [
  { label: "Diabetes reversal", size: "1,240" },
  { label: "PCOS care", size: "860" },
  { label: "GLP-1 program", size: "512" },
  { label: "Weight care · month 1–3", size: "2,105" },
] as const;

export const DURATION_PRESETS = [30, 45, 60, 90] as const;

export const PROVIDER_OPTIONS = [
  // { value: "GoodFlip Live", label: "GoodFlip Live (in-app)" },
  { value: "Zoom", label: "Zoom" },
  { value: "Google Meet", label: "Google Meet" },
] as const;

export const FORMAT_OPTIONS = [
  "Talk + Q&A",
  "Workshop",
  "Group consult",
  "AMA",
] as const;

export const LANG_OPTIONS = ["English", "Hindi", "Marathi", "Tamil"] as const;

export const RECURRENCE_OPTIONS = [
  { value: "none", label: "Does not repeat" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
] as const;

/**
 * Topic chip colors — GoodFlip /patient brand family, but every bg/fg is
 * distinct from STATUS_META so topics never look like status badges.
 * Assignment prefers the first unused entry (see pickTopicColor).
 */
export const TOPIC_PALETTE = [
  { bg: "#E3F4F1", fg: "#2F7D72" }, // teal
  { bg: "#E6F0F8", fg: "#3D6F8F" }, // blue
  { bg: "#EEE8F6", fg: "#6B5B8C" }, // lavender
  { bg: "#F4F0E6", fg: "#8A6F3E" }, // sand
  { bg: "#E8F3FA", fg: "#3A7CA5" }, // sky
  { bg: "#F0EAE3", fg: "#7A5C48" }, // taupe
  { bg: "#EAF3E8", fg: "#4A7A3F" }, // olive
  { bg: "#F5EAF0", fg: "#9A4F78" }, // mauve
  { bg: "#EDF7F2", fg: "#3DB87D" }, // brand-focus mint (not status soft/primary)
  { bg: "#F2F9F6", fg: "#1F6B4D" }, // brand-surface mint text
] as const;

/** Soft bg lookup for legacy topic colors already stored in the API. */
export const LEGACY_TOPIC_SOFT_BG: Record<string, string> = {
  "#5c7a3a": "#edf2e1",
  "#b4527f": "#f2e6ec",
  "#4a7c94": "#e4ecf2",
  "#9a7a3a": "#f2ebe1",
  "#1e7a52": "#e8f2e9",
  "#3f7a72": "#e4ecf2",
};

export const VALIDATION = {
  titleMin: 8,
  titleMax: 70,
  oneLinerMax: 120,
  fullDescMin: 40,
  fullDescMax: 600,
  takeawayMin: 3,
  takeawayMax: 5,
  takeawayCharMax: 90,
  minsMin: 15,
  minsMax: 120,
  minsStep: 5,
  startMinHours: 2,
  bioMax: 240,
  credMax: 4,
  credCharMax: 30,
  joinUrlMin: 4,
  photoMaxBytes: 512 * 1024,
  photoTypes: ["image/jpeg", "image/png"] as string[],
  hostClashMinutes: 90,
};

export const PRIMARY_STYLES: Record<
  SessionStatus,
  { label: string; bg: string; fg: string; border: string }
> = {
  draft: {
    label: "Continue setup",
    bg: "#242220",
    fg: "#FFFFFF",
    border: "#242220",
  },
  scheduled: {
    label: "Coach joining link",
    bg: "#FFFFFF",
    fg: "#238A5E",
    border: "#E7F5EF",
  },
  live: {
    label: "Live console",
    bg: "#299D6B",
    fg: "#FFFFFF",
    border: "#299D6B",
  },
  ended: {
    label: "View report",
    bg: "#FFFFFF",
    fg: "#37322D",
    border: "#ECE7E1",
  },
  cancelled: {
    label: "Reinstate",
    bg: "#FFFFFF",
    fg: "#37322D",
    border: "#ECE7E1",
  },
};
